import React, { useState, useEffect } from 'react';
import { Shield, RefreshCw, Key, AlertCircle, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { connectorsAPI } from '../services/api/client';
import { useI18n } from '../i18n';

const ConnectorsConfig = () => {
  const { t } = useI18n();
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [healthChecking, setHealthChecking] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    loadConnectors();
  }, []);

  const loadConnectors = async () => {
    try {
      setLoading(true);
      const data = await connectorsAPI.list();
      setConnectors(data);
    } catch (err) {
      console.error('Error loading connectors:', err);
    } finally {
      setLoading(false);
    }
  };

  const runHealthCheck = async () => {
    try {
      setHealthChecking(true);
      await connectorsAPI.healthCheck();
      await loadConnectors();
    } catch (err) {
      console.error('Health check error:', err);
    } finally {
      setHealthChecking(false);
    }
  };

  const toggleConnector = async (connectorId, currentEnabled) => {
    try {
      setSaving(connectorId);
      await connectorsAPI.update(connectorId, { is_enabled: !currentEnabled });
      setConnectors(prev => prev.map(c =>
        c.connector_id === connectorId ? { ...c, is_enabled: !currentEnabled } : c
      ));
    } catch (err) {
      console.error('Toggle error:', err);
    } finally {
      setSaving(null);
    }
  };

  const saveApiKey = async (connectorId) => {
    try {
      setSaving(connectorId);
      await connectorsAPI.update(connectorId, { api_key_override: apiKeyInput });
      setEditingKey(null);
      setApiKeyInput('');
    } catch (err) {
      console.error('API key save error:', err);
    } finally {
      setSaving(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'degraded':
        return <AlertCircle className="w-4 h-4 text-yellow-400" />;
      case 'unavailable':
        return <XCircle className="w-4 h-4 text-red-400" />;
      case 'unconfigured':
        return <Key className="w-4 h-4 text-gray-400" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getCostBadge = (costModel) => {
    switch (costModel) {
      case 'free':
        return <span className="px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-300 border border-green-500/30">Gratuit</span>;
      case 'credit_based':
        return <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-500/20 text-yellow-300 border border-yellow-500/30">Crédits</span>;
      case 'subscription':
        return <span className="px-2 py-0.5 text-xs rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/30">Abonnement</span>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-purple-400" />
          <h3 className="text-lg font-semibold text-white">Connecteurs d'enrichissement</h3>
        </div>
        <button
          onClick={runHealthCheck}
          disabled={healthChecking}
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-purple-600/30 hover:bg-purple-600/50 text-purple-200 rounded-lg transition-colors border border-purple-500/30"
        >
          <RefreshCw className={`w-4 h-4 ${healthChecking ? 'animate-spin' : ''}`} />
          {healthChecking ? 'Vérification...' : 'Health Check'}
        </button>
      </div>

      <p className="text-sm text-gray-400">
        Configurez les sources de données pour l'enrichissement OSINT et la recherche de fuites de données.
      </p>

      <div className="space-y-3">
        {connectors.map((connector) => (
          <div
            key={connector.connector_id}
            className="bg-brand-black-light/50 border border-purple-500/20 rounded-lg p-4 space-y-3"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {getStatusIcon(connector.health_status)}
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-white">{connector.display_name}</span>
                    {getCostBadge(connector.cost_model)}
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    {connector.capabilities.map(c => c.replace(/_/g, ' ')).join(' · ')}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {connector.credits_remaining !== null && (
                  <span className="text-xs text-gray-400">
                    {connector.credits_remaining} crédits
                  </span>
                )}
                {connector.monthly_quota && (
                  <span className="text-xs text-gray-400">
                    {connector.used_this_month}/{connector.monthly_quota}
                  </span>
                )}
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={connector.is_enabled}
                    onChange={() => toggleConnector(connector.connector_id, connector.is_enabled)}
                    disabled={saving === connector.connector_id}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>
            </div>

            {/* API Key section */}
            <div className="flex items-center gap-2">
              {editingKey === connector.connector_id ? (
                <div className="flex items-center gap-2 flex-1">
                  <input
                    type="password"
                    value={apiKeyInput}
                    onChange={(e) => setApiKeyInput(e.target.value)}
                    placeholder="Clé API..."
                    className="flex-1 bg-brand-black-light border border-purple-500/30 rounded-lg px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/60"
                  />
                  <button
                    onClick={() => saveApiKey(connector.connector_id)}
                    disabled={saving === connector.connector_id}
                    className="px-3 py-1.5 text-sm bg-purple-600 hover:bg-purple-500 text-white rounded-lg"
                  >
                    {saving === connector.connector_id ? '...' : 'OK'}
                  </button>
                  <button
                    onClick={() => { setEditingKey(null); setApiKeyInput(''); }}
                    className="px-3 py-1.5 text-sm bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => { setEditingKey(connector.connector_id); setApiKeyInput(''); }}
                  className="flex items-center gap-1.5 text-xs text-purple-300 hover:text-purple-200"
                >
                  <Key className="w-3 h-3" />
                  Modifier la clé API
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {connectors.length === 0 && (
        <div className="text-center py-8 text-gray-400">
          Aucun connecteur disponible
        </div>
      )}
    </div>
  );
};

export default ConnectorsConfig;
